import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Crown, ArrowLeft, Medal, Certificate } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/contexts/LanguageContext";
import { ChampionSquare, monthName } from "@/components/quiz/ChampionCard";

function ScaledSquare({ champ, lang }) {
  const wrap = useRef(null);
  const [scale, setScale] = useState(0.3);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setScale(e.contentRect.width / 1080));
    ro.observe(wrap.current);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={wrap} className="w-full rounded-lg overflow-hidden" style={{ height: 1080 * scale }}>
      <div style={{ width: 1080, transform: `scale(${scale})`, transformOrigin: "top left" }}>
        <ChampionSquare champ={champ} lang={lang} />
      </div>
    </div>
  );
}

export default function HallOfFamePage() {
  const { lang } = useTranslation();
  const es = lang === "es";
  const [rows, setRows] = useState(null);

  useEffect(() => {
    axios.get("/api/champions/history", { withCredentials: true }).then((r) => setRows(r.data.champions)).catch(() => setRows([]));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8" data-testid="hall-of-fame-page">
      <Link to="/quizzes">
        <Button variant="ghost" className="text-[#A1A1AA] hover:text-white hover:bg-white/5 -ml-3" data-testid="hall-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          {es ? "Cuestionarios" : "Quizzes"}
        </Button>
      </Link>
      <div>
        <p className="flex items-center gap-1.5 text-[#F5A524] text-xs uppercase tracking-[0.2em] mb-2">
          <Crown weight="fill" className="w-4 h-4" />
          {es ? "Campeones Mensuales" : "Monthly Champions"}
        </p>
        <h1 className="font-['Outfit'] text-4xl sm:text-5xl text-white font-semibold tracking-tight mb-2">{es ? "Salón de la Fama" : "Hall of Fame"}</h1>
        <p className="text-[#71717A] text-base">{es ? "Cada Campeón Mensual de la Clase, del más reciente al más antiguo." : "Every Monthly Class Champion, newest first."}</p>
      </div>
      {rows === null ? (
        <p className="text-[#71717A]">{es ? "Cargando..." : "Loading..."}</p>
      ) : rows.length === 0 ? (
        <p className="text-[#71717A]" data-testid="hall-empty">
          {es ? "Aún no hay campeones. El primero se anuncia al terminar el primer mes completo." : "No champions yet. The first one is announced when the first full month ends."}
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {rows.map((c) => (
            <div key={c.month} className={`bg-[#121214]/80 border rounded-xl p-3 space-y-3 ${c.is_me ? "border-[#F5A524]/60" : "border-white/10"}`} data-testid={`hall-entry-${c.month}`}>
              <ScaledSquare champ={c} lang={lang} />
              <div className="flex items-center justify-between gap-2 px-1">
                <div className="min-w-0">
                  <p className="text-white font-medium truncate">
                    {c.display} {c.is_me && <span className="text-[#F5A524] text-xs">{es ? "(tú)" : "(you)"}</span>}
                  </p>
                  <p className="text-[#71717A] text-xs">{monthName(c.month, lang)}</p>
                </div>
                <div className="flex items-center gap-3 text-xs font-['JetBrains_Mono'] shrink-0">
                  <span className="flex items-center gap-1 text-emerald-300"><Medal weight="fill" className="w-3.5 h-3.5" />{c.badges}</span>
                  <span className="flex items-center gap-1 text-[#F5A524]"><Certificate weight="fill" className="w-3.5 h-3.5" />{c.best_exam || "-"}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
